import {datadogRum} from '@datadog/browser-rum';
import {sequenceT} from 'fp-ts/lib/Apply';
import * as C from 'fp-ts/lib/Console';
import {fromOption} from 'fp-ts/lib/Either';
import {flow} from 'fp-ts/lib/function';
import * as IO from 'fp-ts/lib/IO';
import {IOEither} from 'fp-ts/lib/IOEither';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';

import {OAuthTokenGeneration} from 'Core/appfabric/OAuthTokenGeneration';
import jwtDecode from 'jwt-decode';
import {getOAuthProviderVariablesExternal, getOAuthProviderVariablesInternal} from 'OAuthProviderVariables';
import {fromLocalStorage, fromSessionStorage, LocalStorageKey, toLocalStorage} from 'Shared/utilities/localStorage';
import {parseBoolean, safeParseInt, safeParseJson, safeParseJSONAsAny} from 'Shared/utilities/parsers';
import {getUserClaims} from 'Shared/utilities/UserProfileUtilities';
import {UserClaims} from 'Users/models/Claims';

export const getIsUserEmployee = pipe(
  fromSessionStorage(LocalStorageKey.IsEmployee),
  IO.map(O.fromNullable),
  IO.map(O.map(parseBoolean))
);

export async function refreshToken() {
  if (sessionStorage.getItem('is_employee')?.toLocaleLowerCase() === 'false') {
    toLocalStorage(LocalStorageKey.IsEmployee)('false')();
    sessionStorage.setItem('is_employee', 'false');
    await OAuthTokenGeneration.initiateRefreshFlow(getOAuthProviderVariablesExternal());
  } else {
    toLocalStorage(LocalStorageKey.IsEmployee)('true')();
    sessionStorage.setItem('is_employee', 'true');
    await OAuthTokenGeneration.initiateRefreshFlow(getOAuthProviderVariablesInternal());
  }
}

// We expect the token coming back from the auth service to contain these keys
export type Token = {
  sub?: string;
  Roles?: string;
  exp: number;
};

// Inspect the sub key from the jwt token to set username
export const getUserName = pipe(
  fromLocalStorage(LocalStorageKey.AccessToken),
  IO.map(O.fromNullable),
  IO.map(O.chain(value => O.fromNullable<Token>(jwtDecode(value)))),
  IO.map(O.chain(token => O.fromNullable(token.sub)))
);

/**
 * Inspect the token to flatten/spread the roles key from jwt as claims key
 * Inside the "Roles" claim on the JWT token
 * Spread those userClaims in JSON as a flat object and return
 **/
export const decodeMyAccessClaims = flow(
  (decodedToken: Token) => decodedToken.Roles,
  O.fromNullable,
  O.chain(safeParseJson<UserClaims>()),
  O.map(userClaims => ({...userClaims, key: 'UserClaims'}))
);

export const decodeToken = (accessToken: string): O.Option<Token> => O.tryCatch(() => jwtDecode(accessToken));

/**
 * STATE SETTER
 * access_token as access_token
 * decoded_token.exp * 1000 as expires_at
 * claims as decoded_access_token.Roles.UserClaims
 * @param accessToken
 * @param expiresIn What is this for?
 * @param decodedToken
 */
export const saveAccessToken = (accessToken: string, expiresIn: number, decodedToken: Token, claims: any) => {
  const expiresAt = decodedToken.exp * 1000;
  console.log('saveAccessToken:');

  return sequenceT(IO.io)(
    toLocalStorage(LocalStorageKey.AccessToken)(accessToken),
    toLocalStorage(LocalStorageKey.ExpiresAt)(expiresAt),
    pipe(
      claims,
      O.fold(
        () => C.log('No MyAccess claims found.'),
        myAccessClaims => toLocalStorage('claims')(JSON.stringify(myAccessClaims))
      )
    )
  );
};

export const hasAcceptedTerms = pipe(
  getUserClaims,
  IO.map(
    O.fold(
      () => false,
      claims => (claims as UserClaims).hasAcceptedTermsAndConditions
    )
  )
);

export const isAuthenticated = pipe(
  fromLocalStorage(LocalStorageKey.ExpiresAt),
  IO.map(O.fromNullable),
  IO.map(
    flow(
      O.chain(safeParseJSONAsAny),
      O.map(expiresAt => {
        const hasNotExpired = new Date().getTime() < expiresAt;
        return hasNotExpired;
      })
    )
  ),
  IO.map(O.getOrElse(() => false))
);

export const getAccessToken = pipe(fromLocalStorage(LocalStorageKey.AccessToken), IO.map(O.fromNullable));

export const getExpiresAtTime: IOEither<string, number> = () => {
  const result = pipe(
    fromLocalStorage(LocalStorageKey.ExpiresAt),
    IO.map(O.fromNullable),
    IO.map(O.chain(safeParseInt)),
    IO.map(fromOption(() => 'User session has expired due to inactivity'))
  );
  return result();
};

export function sendDatadogUserInfo() {
  datadogRum.setUser({id: getUserName()['value']});
}
